import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import AdjustStockModal from "../components/stockLevels/AdjustStockModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getStockLevels,
  resetStockLevelServerStatus,
} from "../redux/actions/stockLevelActions";
import StatusChip from "../components/common/StatusChip";
import { StockLevelResponseDto } from "../shared/dtos/stockLevel/StockLevelResponseDto";
import { formatPlainNumber } from "../utils/appUtils";

const StockLevelsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: stockLevels,
    loading: loadingStockLevels,
    error: errorLoadingStockLevels,
    pagination,
    serverStatus,
    loadingAdjust: loadingStockAdjust,
    errorAdjust: errorAdjustingStock,
  } = useAppSelector((state) => state.stockLevel);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState<boolean>(false);
  const [selectedStockLevel, setSelectedStockLevel] =
    useState<StockLevelResponseDto | null>(null);

  const canRead = hasPermission("stock_level:read_all");
  const canUpdate = hasPermission("stock_level:update");

  const fetchStockData = useCallback(() => {
    if (!canRead) return;
    dispatch(
      getStockLevels({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch.trim() || undefined,
        branchId: selectedBranchId || undefined,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    selectedBranchId,
    canRead,
  ]);

  useEffect(() => {
    fetchStockData();
  }, [fetchStockData]);

  useEffect(() => {
    setPageConfig((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearch, selectedBranchId]);

  // --- Modal Handlers ---
  const handleOpenAdjust = (row: StockLevelResponseDto): void => {
    setSelectedStockLevel(row);
    setIsAdjustModalOpen(true);
  };

  const handleCloseAdjust = useCallback(() => {
    if (!loadingStockAdjust) {
      setIsAdjustModalOpen(false);
      setSelectedStockLevel(null);
    }
  }, [loadingStockAdjust]);

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "adjust" && status === 200) {
      handleCloseAdjust();
      fetchStockData();
    }

    dispatch(resetStockLevelServerStatus());
  }, [serverStatus, dispatch, handleCloseAdjust, fetchStockData]);

  const columns: TableColumn<StockLevelResponseDto>[] = useMemo(
    () => [
      {
        id: "itemName",
        label: "Item",
        minWidth: 150,
        format: (_, row) => (
          <Stack sx={{ gap: 0 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {row.itemName}
            </Box>
            <Box
              component="span"
              sx={{ fontSize: "0.75rem", color: "text.secondary" }}
            >
              {row.itemCode}
            </Box>
          </Stack>
        ),
      },
      { id: "category", label: "Category", minWidth: 120 },
      { id: "branchName", label: "Branch", minWidth: 120 },
      {
        id: "quantity",
        label: "Current Stock",
        minWidth: 130,
        align: "right",
        format: (_, row) => (
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {formatPlainNumber(row.quantity)} {row.unitOfMeasure}
          </Typography>
        ),
      },
      {
        id: "id",
        label: "Stock Status",
        minWidth: 120,
        align: "center",
        format: (_, row) => {
          const isLow =
            row.quantity <= (row as any).reorderLevel ||
            row.quantity <= (row as any).orderLevel;
          return <StatusChip status={isLow ? "LOW_STOCK" : "IN_STOCK"} />;
        },
      },
      {
        id: "updatedAt",
        label: "Last Updated",
        minWidth: 150,
        format: (_, row) => new Date(row.updatedAt).toLocaleString(),
      },
      {
        id: "orderLevel",
        label: "Reorder Level",
        minWidth: 130,
        align: "right",
        format: (_, row) => (
          <Typography variant="body2">
            {formatPlainNumber(
              (row as any).reorderLevel ?? (row as any).orderLevel,
            )}{" "}
            {row.unitOfMeasure}
          </Typography>
        ),
      },
    ],
    [],
  );

  const renderRowActions = (row: StockLevelResponseDto): React.JSX.Element => (
    <Stack sx={{ flexDirection: "row", spacing: 1, justifyContent: "center" }}>
      {canUpdate && (
        <Tooltip title="Adjust Stock">
          <IconButton
            size="small"
            color="secondary"
            onClick={() => handleOpenAdjust(row)}
          >
            <SettingsBackupRestoreIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  return (
    <PageContainer
      title="Inventory Stock Levels"
      subtitle="Monitor real-time item quantities and process manual stock adjustments."
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by item name or SKU..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={setSelectedBranchId}
        />
      </PageToolbar>

      <StandardTable<StockLevelResponseDto>
        columns={columns}
        data={stockLevels}
        loading={loadingStockLevels}
        error={errorLoadingStockLevels}
        onRetry={fetchStockData}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(newPage) =>
          setPageConfig((prev) => ({ ...prev, page: newPage }))
        }
        onRowsPerPageChange={(newLimit) =>
          setPageConfig((prev) => ({ ...prev, limit: newLimit, page: 1 }))
        }
        rowAction={canUpdate ? renderRowActions : undefined}
      />

      {isAdjustModalOpen && selectedStockLevel && (
        <AdjustStockModal
          open={isAdjustModalOpen}
          onClose={handleCloseAdjust}
          stockLevel={selectedStockLevel}
          loading={loadingStockAdjust}
          error={errorAdjustingStock}
        />
      )}
    </PageContainer>
  );
};

export default StockLevelsPage;
